import { useCallback, useEffect, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "../../store"
import { useStoreRealtime } from "../realtime/hook"
import { useStoreSession } from "../session/hook"
import { reset_drag, reset_expand, setPos as setPosAction } from "./slice"

export function useStoreLive(init_socket?: boolean) {
  const dispatch = useAppDispatch()
  const { audio, video } = useAppSelector(state => state.live_slice.value)
  const metadata = useAppSelector(state => state.live_slice.metadata)
  const expand = useAppSelector(state => state.live_slice.expand)
  const drag = useAppSelector(state => state.live_slice.drag)
  const pos = useAppSelector(state => state.live_slice.pos)
  const flux = useAppSelector(state => state.live_slice.flux)

  const { session } = useStoreSession()
  const { connect, signin, status } = useStoreRealtime()
  const live_status = status("live")

  const setAudioPos = useCallback((x: number, y: number) => {
    dispatch(setPosAction({ audio: { x, y } }))
  }, [dispatch])

  const setVideoPos = useCallback((x: number, y: number) => {
    dispatch(setPosAction({ video: { x, y } }))
  }, [dispatch])

  const toggleAudio = useCallback((key: "expand" | "drag") => {
    dispatch(
      key === "drag" 
        ? reset_drag({ audio: !drag.audio }) 
        : reset_expand({ audio: !expand.audio })
    )
  }, [dispatch, drag.audio, expand.audio])

  const toggleVideo = useCallback((key: "expand" | "drag") => {
    dispatch(
      key === "drag" 
        ? reset_drag({ video: !drag.video }) 
        : reset_expand({ video: !expand.video })
    )
  }, [dispatch, drag.video, expand.video])

  const audioRes = useMemo(() => ({
    audio,
    flux,
    expand: expand.audio,
    metadata: metadata?.audio,
    drag: drag.audio,
    pos: pos.audio,
    toggle: toggleAudio,
    setPos: setAudioPos
  }), [audio, flux, expand.audio, metadata?.audio, drag.audio, pos.audio, toggleAudio, setAudioPos])

  const videoRes = useMemo(() => ({
    video,
    metadata: metadata?.video,
    expand: expand.video,
    pos: pos.video,
    drag: drag.video,
    toggle: toggleVideo,
    setPos: setVideoPos
  }), [video, metadata?.video, expand.video, pos.video, drag.video, toggleVideo, setVideoPos])

  useEffect(() => {
    if (init_socket && live_status === "disconnected") {
      connect("live")
    }
  }, [init_socket, live_status, connect])

  useEffect(() => {
    if (live_status === "connected" && session) {
      signin("live", session)
    }
  }, [live_status, session, signin])

  return useMemo(() => ({
    audio: audioRes,
    video: videoRes,
  }), [audioRes, videoRes])
}